/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import {
  type RowData,
  type Table as DataGridType,
} from '@tanstack/react-table';
import classNames from 'classnames';
import {
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { type CommonProps, type HtmlAttributes } from '../_common/types';
import { useResizeObserver } from '../hooks';
import { type Skeleton } from '../skeleton';
import {
  ActionButton,
  ActionCell,
  Body,
  BodyCell,
  BodyRow,
  ColumnControls,
  DropDownCell,
  Header,
  HeaderCell,
  HeaderTitle,
  InlineEditCell,
  LoadingPlaceholder,
  Navigation,
  NoDataIcon,
  NoDataPlaceholder,
  NoDataPlaceholderContentWrapper,
  Pagination,
  PaginationArrowButton,
  PaginationNumericButton,
  PaginationNumericButtons,
  ResizingBar,
  RowsPerPage,
  Scrollable,
  TableResults,
} from './Components';
import {
  type DataGridComponentsInterface,
  DataGridContext,
  type DataGridContextType,
} from './data-grid-context';
import { useTableNav } from './data-grid-nav';
import { updateColumnWidths } from './helpers';

enum BorderStyleEnum {
  AllSides = 'all-sides',
  Horizontal = 'horizontal',
  Vertical = 'vertical',
  None = 'none',
}

export type BorderStyle = `${BorderStyleEnum}`;

enum HeaderStyleEnum {
  Filled = 'filled',
  Clean = 'clean',
}

export type HeaderStyle = `${HeaderStyleEnum}`;

export type DataGridProps<T extends RowData> = {
  styling?: Partial<{
    /** Alternate the background color of rows */
    hasZebraStriping: boolean;
    /** Type of border that will be used */
    borderStyle: BorderStyle;
    /** If header should have styles or not (Aura example) */
    headerStyle: HeaderStyle;
    /** If background hover effects should be enabled on rows and header cells */
    hasHoverEffects: boolean;
  }>;
  /** Resizing the width of columns */
  isResizable?: boolean;
  /** Enables table's loading state */
  isLoading?: boolean;
  /**
   * Enables keyboard navigation.
   * To control keyboard navigation programmatically
   * for edge cases, pass a `ref` to `tableNavRef`
   */
  isKeyboardNavigable?: boolean;
  /**
   * Enables automatic resizing of columns.
   * If `isResizable` is `true`, the columns will only be automatically resized on initial render of the table.
   * If `isResizable` is `false`, the columns will be resized automatically to always fit the table width (assuming that column maxWidths allow for it).
   * Note: Action columns are a fixed size of 40px, and Pinned columns are not resized automatically.
   * @defaultValue true
   */
  isAutoResizingColumns?: boolean;
  /**
   * Reference to the tableNav instance
   * for programmatic control
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tableNavRef?: React.RefObject<any>;
  /** Table instance created from `useTable`*/
  tableInstance: DataGridType<T>;
  /** Table sub-component overrides */
  components?: DataGridComponentsInterface<T>;
  /** @deprecated Use htmlAttributes, className and style on the root element instead */
  rootProps?: HtmlAttributes<'div'>;
  isSkeletonLoading?: boolean;
  skeletonProps?: React.ComponentProps<typeof Skeleton>;
  /** The target element for portaled elements to be rendered in, e.g. the modal root if the data grid is inside a modal.
   * This is used in for example the DropdownCell, RowsPerPage, and ColumnControls.
   */
  portalTarget?: HTMLElement | null;
  ref?: React.Ref<HTMLDivElement>;
};

const DEFAULT_STYLING: Required<DataGridProps<unknown>['styling']> = {
  borderStyle: BorderStyleEnum.Horizontal,
  hasHoverEffects: true,
  hasZebraStriping: false,
  headerStyle: HeaderStyleEnum.Filled,
};

const DataGridComponent = function DataGrid<T extends RowData = unknown>({
  isResizable = true,
  isLoading = false,
  isKeyboardNavigable = true,
  isAutoResizingColumns = true,
  tableInstance,
  components,
  rootProps,
  styling = {},
  tableNavRef,
  isSkeletonLoading = false,
  skeletonProps,
  ref,
  portalTarget = document.body,
  htmlAttributes,
  className,
  style,
  ...restProps
}: CommonProps<'div', DataGridProps<T>>) {
  const { className: rootClassName = '', style: rootStyle = {} } =
    rootProps || {};

  const [isScrollable, setIsScrollable] = useState(false);
  const [isMediumNavigation, setIsMediumNavigation] = useState(false);
  const [isSmallNavigation, setIsSmallNavigation] = useState(false);
  const tableRef = useRef<HTMLDivElement>(null);
  const scrollableContainerRef = useRef<HTMLDivElement>(null);

  tableInstance.setOptions((prevOptions) => {
    return {
      ...prevOptions,
      enableSorting: isSkeletonLoading ? false : prevOptions.enableSorting,
    };
  });

  // Triggers when the whole container is resized
  useResizeObserver({
    onResize: (entry) => {
      if (entry.width === undefined) {
        return;
      }
      isAutoResizingColumns &&
        !isResizable &&
        updateColumnWidths(tableInstance, entry.width); // don't resize columns when the table is resizable due to problems with the resize handler.
      setIsScrollable(tableInstance.getTotalSize() > entry.width);
      setIsMediumNavigation(entry.width <= 680);
      setIsSmallNavigation(entry.width <= 480);
    },
    ref: scrollableContainerRef as React.RefObject<HTMLElement>,
  });

  // Triggers when the individual columns are resized
  useResizeObserver({
    onResize: () => {
      if (scrollableContainerRef.current === null) {
        return;
      }
      setIsScrollable(
        tableInstance.getTotalSize() >
          scrollableContainerRef.current.clientWidth,
      );
    },
    ref: tableRef as React.RefObject<HTMLElement>,
  });

  const Styling = useMemo(
    () => ({
      ...DEFAULT_STYLING,
      ...styling,
    }),
    [styling],
  );

  const Components = useMemo(
    () =>
      ({
        ...DataGridComponents,
        ...components,
      }) as DataGridContextType<T>['components'],
    [components],
  );
  const ScrollableContainer = Components?.Scrollable
    ? Components.Scrollable
    : Scrollable;

  const { listeners, tableNav } = useTableNav({
    isDebug: false,
  });

  /** Surface the tableNav instance for programmatic control if needed */
  useImperativeHandle(tableNavRef, () => tableNav, [tableNav]);

  useEffect(() => {
    if (isAutoResizingColumns && isResizable) {
      updateColumnWidths(
        tableInstance,
        scrollableContainerRef.current?.clientWidth
          ? scrollableContainerRef.current.clientWidth - 1 // -1 to avoid horizontal scrollbar
          : 0,
      );
    }
  }, [isAutoResizingColumns, tableInstance, isResizable]);

  return (
    <DataGridContext.Provider
      value={
        {
          components: Components,
          dataGridNav: tableNav,
          hasResizeColumns: isAutoResizingColumns,
          isKeyboardNavigationEnabled: isKeyboardNavigable,
          isLoading: isLoading,
          isMediumNavigation,
          isResizable: isResizable,
          isSkeletonLoading,
          isSmallNavigation,
          portalTarget: portalTarget,
          skeletonProps,
          tableProps: tableInstance,
        } as DataGridContextType<T>
      }
    >
      <div
        // Root props needs to be first in order to override className
        {...rootProps}
        className={classNames('ndl-data-grid-root', rootClassName, className, {
          'ndl-data-grid-border-horizontal':
            Styling.borderStyle === BorderStyleEnum.Horizontal ||
            Styling.borderStyle === BorderStyleEnum.AllSides,
          'ndl-data-grid-border-vertical':
            Styling.borderStyle === BorderStyleEnum.Vertical ||
            Styling.borderStyle === BorderStyleEnum.AllSides,
          'ndl-data-grid-focusable-cells': isKeyboardNavigable,
          'ndl-data-grid-header-filled':
            Styling.headerStyle === HeaderStyleEnum.Filled,
          'ndl-data-grid-hover-effects': Styling.hasHoverEffects,
          'ndl-data-grid-zebra-striping': Styling.hasZebraStriping,
        })}
        ref={ref}
        style={{ ...rootStyle, ...style }}
        {...restProps}
        {...htmlAttributes}
        {...(isKeyboardNavigable ? listeners : {})}
      >
        {/* Make DataGrid horizontally scrollable and avoid horizontal scrollbar have an extra vertical scrollbar (esp. Safari) */}
        {/* This behavior can be overridden by consumers through replacing the scrolling container */}
        <ScrollableContainer
          ref={scrollableContainerRef}
          htmlAttributes={{
            onKeyDown: (e) => {
              if (isScrollable && !isKeyboardNavigable) {
                if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
                  e.stopPropagation();
                }
              }
            },
            // Ensure that the user can focus and scroll inside the table with arrow keys even if there is no interactive element inside
            tabIndex: isScrollable && !isKeyboardNavigable ? 0 : undefined,
          }}
        >
          {/* Table content Area */}
          <div
            className="ndl-div-table"
            role={isKeyboardNavigable ? 'grid' : 'table'}
            aria-busy={isLoading ? 'true' : 'false'}
            ref={tableRef}
          >
            {/* DataGrid Header Area */}
            {Components?.Header && <Components.Header />}
            {Components?.Body && <Components.Body />}
          </div>
        </ScrollableContainer>
        {/* Bottom Navigation Area */}
        {Components?.Navigation && <Components.Navigation />}
      </div>
    </DataGridContext.Provider>
  );
};

const DataGridComponents = {
  ActionButton,
  ActionCell,
  Body,
  BodyCell,
  BodyRow,
  ColumnControls,
  DropDownCell,
  Header,
  HeaderCell,
  HeaderTitle,
  InlineEditCell,
  LoadingPlaceholder,
  Navigation,
  NoDataIcon,
  NoDataPlaceholder,
  NoDataPlaceholderContentWrapper,
  Pagination,
  PaginationArrowButton,
  PaginationNumericButton,
  PaginationNumericButtons,
  ResizingBar,
  RowsPerPage,
  Scrollable,
  TableResults,
};

const DataGrid = Object.assign(DataGridComponent, DataGridComponents);

export { DataGrid };
